function canOverflowScroll(overflow: string): boolean {
  return overflow === 'auto' || overflow === 'scroll' || overflow === 'overlay';
}

export function isHorizontallyScrollable(el: HTMLElement): boolean {
  if (!canOverflowScroll(getComputedStyle(el).overflowX)) {
    return false;
  }
  return el.scrollWidth > el.clientWidth;
}

export function isVerticallyScrollable(el: HTMLElement): boolean {
  if (!canOverflowScroll(getComputedStyle(el).overflowY)) {
    return false;
  }
  return el.scrollHeight > el.clientHeight;
}

/** overflow-x auto/scroll, including when content currently fits. */
export function isHorizontalScrollPort(el: HTMLElement): boolean {
  return canOverflowScroll(getComputedStyle(el).overflowX);
}

/**
 * Horizontal chip/table ports that are not themselves vertical scrollers.
 * overflow-x: auto still captures Android pans when content fits.
 */
export function isHorizontalOnlyScrollPort(el: HTMLElement): boolean {
  return isHorizontalScrollPort(el) && !isVerticallyScrollable(el);
}

export function findNearestVerticalScroller(target: EventTarget | null): HTMLElement | null {
  if (!(target instanceof Element)) {
    return null;
  }
  let current: Element | null = target;
  while (current instanceof HTMLElement) {
    if (isVerticallyScrollable(current)) {
      return current;
    }
    current = current.parentElement;
  }
  return null;
}

export function findHorizontalOnlyScrollPort(target: EventTarget | null): HTMLElement | null {
  if (!(target instanceof Element)) {
    return null;
  }
  let current: Element | null = target;
  while (current instanceof HTMLElement) {
    if (isHorizontalOnlyScrollPort(current)) {
      return current;
    }
    current = current.parentElement;
  }
  return null;
}

function documentVerticalScroller(): HTMLElement | null {
  const scrolling = document.scrollingElement;
  if (!(scrolling instanceof HTMLElement)) {
    return null;
  }
  if (scrolling.scrollHeight > scrolling.clientHeight) {
    return scrolling;
  }
  return null;
}

/**
 * Parent vertical scroller to drive when a pan starts on a horizontal-only port
 * (chip rows, location tables). Native Y does not chain out of those ports.
 */
export function findVerticalScrollerForForward(target: EventTarget | null): HTMLElement | null {
  if (findHorizontalOnlyScrollPort(target) === null) {
    return null;
  }
  return findNearestVerticalScroller(target) ?? documentVerticalScroller();
}

export function applyVerticalScroll(scroller: HTMLElement, deltaY: number): void {
  scroller.scrollTop -= deltaY;
}

/**
 * Walks ancestors for an overflow-x scroller that can consume `deltaX`.
 * Finger right (deltaX > 0) scrolls content left (needs scrollLeft > 0).
 * Finger left (deltaX < 0) scrolls content right (needs remaining scroll).
 */
export function findConsumingHorizontalScroller(
  target: EventTarget | null,
  deltaX: number,
): HTMLElement | null {
  if (!(target instanceof Element) || deltaX === 0) {
    return null;
  }
  let current: Element | null = target;
  while (current instanceof HTMLElement) {
    if (isHorizontallyScrollable(current)) {
      const canScrollLeft = current.scrollLeft > 0;
      const canScrollRight =
        current.scrollLeft + current.clientWidth < current.scrollWidth - 1;
      if ((deltaX > 0 && canScrollLeft) || (deltaX < 0 && canScrollRight)) {
        return current;
      }
    }
    current = current.parentElement;
  }
  return null;
}

/**
 * Walks ancestors for an overflow-y scroller that can consume `deltaY`.
 * Finger down (deltaY > 0) scrolls content up (needs scrollTop > 0).
 * Finger up (deltaY < 0) scrolls content down (needs remaining scroll).
 */
export function findConsumingVerticalScroller(
  target: EventTarget | null,
  deltaY: number,
): HTMLElement | null {
  if (!(target instanceof Element) || deltaY === 0) {
    return null;
  }
  let current: Element | null = target;
  while (current instanceof HTMLElement) {
    if (isVerticallyScrollable(current)) {
      const canScrollUp = current.scrollTop > 0;
      const canScrollDown =
        current.scrollTop + current.clientHeight < current.scrollHeight - 1;
      if ((deltaY > 0 && canScrollUp) || (deltaY < 0 && canScrollDown)) {
        return current;
      }
    }
    current = current.parentElement;
  }
  return null;
}
